import type { ChatMessage } from '@elara/client-core/models/ChatMessageModel';
import type { MessagePart } from '@elara/domain/models/schemas/message-schemas';

import { isTextPart, isWidgetPart } from '@elara/domain/models/schemas/message-schemas';

// `mergeAdjacentTextParts` is pure message-content logic shared with MessageUiState (and the RN
// client), so it now lives in @elara/client-core (ELARA-754). Re-exported here so existing
// `#ui/chat/utils` importers are unchanged.
export { mergeAdjacentTextParts } from '@elara/client-core/chat/message-parts';
// `getFallbackCoverImageIcon` moved to @elara/client-core (ELARA-708 follow-up) so the mobile card
// port uses the same fallback-icon logic web does. Re-exported here so existing importers are unchanged.
export { getFallbackCoverImageIcon } from '@elara/client-core/lib/cover-image-icon';

/**
 * Extracts and concatenates all text parts from a message's content array.
 * Skips widget parts since they are rendered separately.
 * @param content - The content array of a message.
 * @returns The concatenated text, or undefined if no text parts are found.
 */
export function extractText(content: MessagePart[]): string | undefined {
  const buf: string[] = [];

  if (!Array.isArray(content)) {
    console.warn('[extractText] Content is not an array:', content);
    return;
  }

  for (const part of content) {
    if (!part || typeof part !== 'object') continue;
    if (isWidgetPart(part)) continue;
    if (isTextPart(part)) buf.push(part.text);
  }

  return buf.length ? buf.join('') : undefined;
}

/**
 * Formats message text, handling error messages specially
 * @param text - The raw message text
 * @param msg - The message object
 * @returns The formatted text
 * @private
 */
export function formatMessageText(
  text: string,
  msg: ChatMessage & { isOptimistic?: boolean; _status?: string },
): string {
  // Check if this is an error message from the AI
  if (msg.role === 'model' && msg.metadata?.error) {
    // Extract a user-friendly error message
    const errorType = msg.metadata.errorType as string;

    const isRuntimeError =
      errorType === 'GenkitError' || // legacy persisted metadata
      errorType === 'RuntimeError' ||
      errorType === 'ProviderError';

    if (isRuntimeError || text.includes('encountered an error')) {
      // For runtime/provider failures, return the user-facing sentence only.
      const mainMessage = text.split('.')[0] + '.';
      return mainMessage;
    }
  }

  return text;
}

/**
 * Detects MIME type from image URL based on file extension.
 *
 * @param url - The image URL to analyze
 * @returns The detected MIME type string (defaults to 'image/jpeg' if detection fails)
 */
export function detectImageMimeType(url: string): string {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();

    // Extract file extension
    const extension = pathname.split('.').pop();

    switch (extension) {
      case 'jpg':
      case 'jpeg': {
        return 'image/jpeg';
      }
      case 'png': {
        return 'image/png';
      }
      case 'gif': {
        return 'image/gif';
      }
      case 'webp': {
        return 'image/webp';
      }
      case 'svg': {
        return 'image/svg+xml';
      }
      case 'bmp': {
        return 'image/bmp';
      }
      case 'tiff':
      case 'tif': {
        return 'image/tiff';
      }
      case 'ico': {
        return 'image/x-icon';
      }
      case 'avif': {
        return 'image/avif';
      }
      default: {
        // Default to JPEG if extension is not recognized
        return 'image/jpeg';
      }
    }
  } catch {
    // If URL parsing fails, default to JPEG
    console.warn('[detectImageMimeType] Failed to parse URL for MIME type detection:', url);
    return 'image/jpeg';
  }
}
